import { Collapsible, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CollapsibleTextProps {
  text: string;
  collapsedLines?: number;
  className?: string;
  lineHeight?: string;
  transitionDuration?: number;
}
// TODO: Needs to enhance with better approach
export const CollapsibleText = ({
  text,
  collapsedLines = 3,
  className,
  lineHeight = '1.75rem',
  transitionDuration = 0,
}: CollapsibleTextProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const [applyClamp, setApplyClamp] = useState(true);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const textRef = useRef<HTMLParagraphElement>(null);
  const { t } = useTranslation();

  const collapsedHeight = parseFloat(lineHeight) * 16 * collapsedLines + 'px';

  useLayoutEffect(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    const checkClamp = () => {
      const originalMaxHeight = element.style.maxHeight;
      element.style.maxHeight = 'none';

      setContentHeight(element.scrollHeight);
      const isContentClamped = element.scrollHeight > element.clientHeight;
      setIsClamped(isContentClamped);

      element.style.maxHeight = originalMaxHeight;
    };

    checkClamp();

    window.addEventListener('resize', checkClamp);

    return () => window.removeEventListener('resize', checkClamp);
  }, [text]);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setApplyClamp(true);
      }, transitionDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, transitionDuration]);

  const handleOpenChange = (openState: boolean) => {
    if (openState) {
      setApplyClamp(false);
    }
    setIsOpen(openState);
  };

  const expandedHeight = contentHeight ? `${contentHeight}px` : 'none';

  const lineClampStyle = {
    '--line-clamp-lines': collapsedLines,
  } as CSSProperties;

  const transitionStyle = {
    transitionDuration: `${transitionDuration}ms`,
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      className="flex flex-col items-start gap-2 w-full"
    >
      <p
        ref={textRef}
        style={{
          maxHeight: isOpen ? expandedHeight : collapsedHeight,
          ...lineClampStyle,
          ...transitionStyle,
        }}
        className={cn(
          'overflow-hidden transition-[max-height] ease-in-out',
          !isOpen && applyClamp && 'line-clamp-(--line-clamp-lines)',
          className
        )}
      >
        {text}
      </p>

      {isClamped && (
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="h-auto hover:bg-transparent p-0 text-primary hover:text-primary font-semibold gap-1"
          >
            {isOpen ? t('common.show_less') : t('common.show_more')}
            <ChevronDown
              className={cn('h-4 w-4 transition-transform duration-300', isOpen && 'rotate-180')}
            />
          </Button>
        </CollapsibleTrigger>
      )}
    </Collapsible>
  );
};
